import axios from 'axios';
import { Check, PencilLine, Plus, X, Trash2, Save } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { GenericException } from 'base/ui/errors';
import { Popup } from 'base/ui/popups';
import { LoadingOverlay, NotFoundView } from 'base/ui/status';
import { broadcaster, useBroadcastedState } from 'base/utils/events';

import CatalogProduct from './product/CatalogProduct';
import ProductSubSelections from './product/ProductSubSelections';
import { CUSTOM_DISCOUNT_CODE } from '../../../constants';
import { getCartTotal, getDisplayPrice } from '../../../helpers/StoreHelper';

// Component for managing items in popup
function ManageItemsPopup({ 
    initialItems, 
    cart, 
    cart_items, 
    allProducts, 
    setAllProducts, 
    processed_request_ref,
    onClose 
}) {
    // Helper function to get cart count for a product name
    const getCartCountForItem = (product_name) => {
        const requestData = cart?.data?.make_my_order_request?.[product_name];
        const requested_cp_id = requestData?.cp_id;
        if (requested_cp_id && cart_items[requested_cp_id]) {
            return cart_items[requested_cp_id].diff_qty || 0;
        }
        return 0;
    }

    // Helper function to check if item has products in cart
    const hasInCartForItem = (product_name) => {
        return getCartCountForItem(product_name) > 0;
    }

    const [managedItems, setManagedItems] = useState(
        initialItems.map((product_name, idx) => ({
            id: `item-${product_name}-${idx}`,
            name: product_name,
            originalName: product_name, // Track original name for mapping
            isEditing: false,
            editedName: product_name
        }))
    );
    const [newItemName, setNewItemName] = useState('');
    const manageItemsInputRef = useRef(null);

    const startEditingItem = (index) => {
        const item = managedItems[index];
        // Use originalName for cart lookup
        const cartCount = getCartCountForItem(item.originalName);
        
        if (cartCount > 0) {
            Popup.toast(
                <div className='tw-rounded tw-p-2 tw-text-sm'>Cannot edit item name. Related product is already in cart.</div>,
                2000
            );
            return;
        }
        
        const updated = managedItems.map((item, i) => 
            i === index ? { ...item, isEditing: true, editedName: item.name } : item
        );
        setManagedItems(updated);
        setTimeout(() => {
            manageItemsInputRef.current?.focus();
        }, 0);
    }

    const cancelEditingItem = (index) => {
        const updated = managedItems.map((item, i) => 
            i === index ? { ...item, isEditing: false, editedName: item.name } : item
        );
        setManagedItems(updated);
    }

    const saveItemEdit = (index) => {
        const item = managedItems[index];
        const newName = item.editedName.trim();

        if (!newName || newName === item.name) {
            cancelEditingItem(index);
            return;
        }

        // Check for duplicates (case-insensitive, excluding current item)
        const normalizedNewName = newName.toLowerCase();
        const duplicateExists = managedItems.some((itm, i) => 
            i !== index && itm.name.toLowerCase() === normalizedNewName
        );

        if (duplicateExists) {
            Popup.toast(
                <div className='tw-rounded tw-p-2 tw-text-sm'>This item already exists in the list.</div>,
                1500
            );
            return;
        }

        // Update the item name (preserve originalName for mapping)
        const updated = managedItems.map((itm, i) => 
            i === index ? { ...itm, name: newName, isEditing: false, editedName: newName, originalName: itm.originalName } : itm
        );
        setManagedItems(updated);
    }

    const removeItem = (index) => {
        const item = managedItems[index];
        
        // Use originalName for cart lookup
        if (hasInCartForItem(item.originalName)) {
            Popup.toast(
                <div className='tw-rounded tw-p-2 tw-text-sm'>Cannot remove item. Related product is already in cart.</div>,
                2000
            );
            return;
        }

        const updated = managedItems.filter((_, i) => i !== index);
        setManagedItems(updated);
    }

    const addNewItemInPopup = () => {
        const trimmedName = newItemName.trim();
        if (!trimmedName) {
            Popup.toast(
                <div className='tw-rounded tw-p-2 tw-text-sm'>Please enter a product name.</div>,
                1500
            );
            return;
        }

        // Check for duplicates (case-insensitive)
        const normalizedName = trimmedName.toLowerCase();
        const duplicateExists = managedItems.some(item => 
            item.name.toLowerCase() === normalizedName
        );

        if (duplicateExists) {
            Popup.toast(
                <div className='tw-rounded tw-p-2 tw-text-sm'>This item already exists in the list.</div>,
                1500
            );
            return;
        }

        setManagedItems([...managedItems, {
            id: `item-${trimmedName}-${Date.now()}`,
            name: trimmedName,
            originalName: trimmedName, // For new items, originalName is the same as name
            isEditing: false,
            editedName: trimmedName
        }]);
        setNewItemName('');
    }

    const saveAllChanges = () => {
        // Build updated make_my_order_request preserving order
        const oldRequest = cart?.data?.make_my_order_request || {};
        const updatedMakeMyOrderRequest = {};
        
        // Iterate through managedItems in order to preserve position
        managedItems.forEach(item => {
            // Use originalName to find the data in oldRequest
            const existingData = oldRequest[item.originalName] || {};
            updatedMakeMyOrderRequest[item.name] = existingData;
        });

        // Update cart
        const updatedCart = {
            ...cart,
            data: {
                ...cart.data,
                make_my_order_request: updatedMakeMyOrderRequest
            }
        };

        // Reset processed_request_ref to allow useEffect to re-run
        processed_request_ref.current = null;

        // Update allProducts to match managed items (preserving order)
        const updatedAllProducts = {};
        managedItems.forEach(item => {
            // Use originalName to get data from allProducts, fallback to current name
            updatedAllProducts[item.name] = allProducts[item.originalName] || allProducts[item.name] || [];
        });
        setAllProducts(updatedAllProducts);

        broadcaster.broadcast_event("cart_updated", updatedCart);
    }

    return (
        <div className='tw-p-4 tw-max-h-[70vh] tw-overflow-y-auto'>
            <div className='tw-space-y-3 tw-mb-4'>
                {managedItems.map((item, index) => {
                    // Use originalName for cart lookups since cart data is still keyed by original name
                    const itemHasInCart = hasInCartForItem(item.originalName);
                    const cartCount = getCartCountForItem(item.originalName);
                    const editingIndex = managedItems.findIndex(i => i.isEditing);
                    
                    return (
                        <div 
                            key={item.id}
                            className='tw-flex tw-items-center tw-gap-2 tw-p-3 tw-bg-gray-50 tw-rounded-lg tw-border tw-border-gray-200'
                        >
                            {item.isEditing ? (
                                <>
                                    <input
                                        ref={index === editingIndex ? manageItemsInputRef : null}
                                        type='text'
                                        value={item.editedName}
                                        onChange={(e) => {
                                            const updated = managedItems.map((itm, i) => 
                                                i === index ? { ...itm, editedName: e.target.value } : itm
                                            );
                                            setManagedItems(updated);
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                saveItemEdit(index);
                                            } else if (e.key === 'Escape') {
                                                cancelEditingItem(index);
                                            }
                                        }}
                                        className='tw-flex-1 tw-px-3 tw-py-1.5 tw-text-sm tw-border tw-border-blue-500 tw-rounded tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-300'
                                    />
                                    <button
                                        type='button'
                                        onClick={() => saveItemEdit(index)}
                                        className='tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-bg-green-600 tw-text-white tw-rounded hover:tw-bg-green-700 tw-transition-colors'
                                    >
                                        <Check size={16} />
                                    </button>
                                    <button
                                        type='button'
                                        onClick={() => cancelEditingItem(index)}
                                        className='tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-text-gray-700 hover:tw-text-gray-900'
                                    >
                                        <X size={16} />
                                    </button>
                                </>
                            ) : (
                                <>
                                    <span className='tw-flex-1 tw-text-sm tw-font-medium tw-text-gray-900'>
                                        {item.name}
                                        {itemHasInCart && (
                                            <span className='tw-ml-2 tw-text-xs tw-text-green-600 tw-font-normal'>
                                                ({cartCount} in cart)
                                            </span>
                                        )}
                                    </span>
                                    <button
                                        type='button'
                                        onClick={() => startEditingItem(index)}
                                        disabled={itemHasInCart}
                                        className={`tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-transition-colors ${
                                            itemHasInCart 
                                                ? 'tw-text-gray-300 tw-cursor-not-allowed' 
                                                : 'tw-text-gray-500 hover:tw-text-gray-700'
                                        }`}
                                        title={itemHasInCart ? 'Cannot edit item name with products in cart' : 'Edit item name'}
                                    >
                                        <PencilLine size={16} />
                                    </button>
                                    <button
                                        type='button'
                                        onClick={() => removeItem(index)}
                                        disabled={itemHasInCart}
                                        className={`tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-rounded tw-transition-colors ${
                                            itemHasInCart 
                                                ? 'tw-text-gray-300 tw-cursor-not-allowed' 
                                                : 'tw-text-red-500 hover:tw-text-red-700 hover:tw-bg-red-50'
                                        }`}
                                        title={itemHasInCart ? 'Cannot remove item with products in cart' : 'Remove item'}
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Add New Item */}
            <div className='tw-mb-4 tw-p-3 tw-bg-gray-50 tw-rounded-lg tw-border tw-border-gray-200'>
                <div className='tw-text-xs tw-font-medium tw-text-gray-700 tw-mb-2'>Add New Item</div>
                <div className='tw-flex tw-gap-2'>
                    <input
                        type='text'
                        value={newItemName}
                        onChange={(e) => setNewItemName(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                addNewItemInPopup();
                            }
                        }}
                        placeholder='Enter product name...'
                        className='tw-flex-1 tw-px-3 tw-py-1.5 tw-text-sm tw-border tw-border-gray-300 tw-rounded tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-300'
                    />
                    <button
                        type='button'
                        onClick={addNewItemInPopup}
                        className='tw-px-4 tw-py-1.5 tw-bg-secondary-lighter tw-text-white tw-rounded hover:tw-bg-secondary-lighter/90 tw-transition-colors tw-text-sm tw-font-medium'
                    >
                        <Plus size={16} />
                    </button>
                </div>
            </div>

            {/* Save Button */}
            <div className='tw-flex tw-gap-2'>
                <button
                    type='button'
                    onClick={onClose}
                    className='tw-flex-1 tw-px-4 tw-py-2 tw-bg-gray-200 tw-text-gray-700 tw-rounded hover:tw-bg-gray-300 tw-transition-colors tw-font-medium'
                >
                    Cancel
                </button>
                <button
                    type='button'
                    onClick={saveAllChanges}
                    className='tw-flex-1 tw-px-4 tw-py-2 tw-bg-secondary-lighter tw-text-white tw-rounded hover:tw-bg-secondary-lighter/90 tw-transition-colors tw-font-medium tw-flex tw-items-center tw-justify-center tw-gap-2'
                >
                    <Save size={16} />
                    Save
                </button>
            </div>
        </div>
    );
}

/**
 * 
 * @param {buying_for_user} buying_for_user - if buying for user mode
 * @param {cart} cart - mandatory
 * @param {delivery_route} delivery_route - mandatory
//  * @param {user} user - mandatory
 * @param {onViewProduct} onViewProduct - handler on clicking a product, skip if product details need not be opened from cart
 * @param {onRefresh} onRefresh - handler on deletion of active order.
 * @param {is_from_store} is_from_store - default is false. If true, products/discounts can be added from cart.
 * @returns Build Cart View
 */
function MakeMyCart({
    cart: _cart,
    delivery_route,
    user,
    buying_for_user,
    onViewProduct,
    onRefresh,
    is_from_store,
    container,
}) {
    const [cart, setCart] = useBroadcastedState("cart_updated", _cart || {});
    const cart_items = cart?.items || {};
    const { total_amount, total_saving, items_total, cp_ids, abandoned_cp_ids } = getCartTotal(null, cart, false);
    const view_ref = useRef();
    const currency = cart?.pricing?.currency || delivery_route?.currency;

    const ctx = useRef({ custom_discount: {}, delivery_time_ranges: null }).current;
    const [is_loading, setLoading] = useState(false);
    const [allProducts, setAllProducts] = useState({});
    const [editingProductName, setEditingProductName] = useState(null);
    const [editedProductName, setEditedProductName] = useState('');
    const editInputRef = useRef(null);
    const sectionRefs = useRef({});

    const updateCartDelivery = (payload) => {
        if (is_loading) return;
        setLoading(true);
        axios.post(
            `/api/cart/diff/${delivery_route._id}`,
            {
                cart_id: cart._id,
                buying_for_user_id: buying_for_user?._id,
                delivery_point_name: cart.delivery_point_name,
                delivery_time: cart.delivery_time,
                address: cart?.address,
                ...payload
            }
        ).then(
            (resp) => {
                if (resp.data.errors) {
                    Popup.show("Error Occurred!", <GenericException ex={resp.data.errors} />)
                }
                /* main order updated */
                resp.data.order
                    && broadcaster.broadcast_event("order_updated", resp.data.order);

                const _cart = resp.data.cart || { ...cart };
                if (resp.data.updated_items) {
                    Object.assign(_cart.items, resp.data.updated_items);
                }
                if (resp.data.pricing) {
                    _cart.pricing = resp.data.pricing;
                }
                if (resp.data.delivery_time_ranges) {
                    ctx.delivery_time_ranges = resp.data.delivery_time_ranges;
                }
                broadcaster.broadcast_event("cart_updated", _cart);
            }
        ).finally(() => setLoading(false));
    }

    const addProduct = ({ products }) => {
        if (products?.length) {
            const cp = products[0];
            if (!cp.qty || cp.qty < 0) {
                Popup.toast(
                    <div className='tw-rounded tw-p-2 tw-text-sm'>Item selected is out of stock.</div>,
                    2500
                )
                return;
            }
            if (cp.sub_selections?.length) {
                const configs = { ...(cart_items?.[cp._id]?.configs || {}) }
                const popup = Popup.slideUp(
                    <ProductSubSelections
                        delivery_route={delivery_route}
                        sub_selections={cp.sub_selections}
                        onAdd={(config) => {
                            config.qty = 1;
                            updateCartDelivery({
                                catalog_product_id: cp._id,
                                diff_qty: 1,
                                config, config_index: Object.keys(configs || {}).length + 1
                            });
                            popup.close();
                        }}
                    />,
                    { container }
                )
                return;
            }
            updateCartDelivery({
                catalog_product_id: products[0]._id,
                diff_qty: 1,
            })
        }
    }

    const processed_request_ref = useRef(null);

    useEffect(() => {
        if (cart?.data?.make_my_order_request && cart?.delivery_route_id) {
            const searchable_product_names = Object.keys(cart.data.make_my_order_request);

            if (!searchable_product_names.length) {
                return;
            }

            // Check if we've already processed this request
            const request_key = JSON.stringify(cart.data.make_my_order_request);
            if (processed_request_ref.current === request_key) {
                return;
            }

            // Reset processed_request_ref to allow re-processing when new product names are added
            processed_request_ref.current = request_key;

            // Set loading state
            setLoading(true);

            // Make parallel API calls for all product names
            const searchPromises = searchable_product_names.map(product_name =>
                axios.post(`/api/products/search/${cart.delivery_route_id}`, {
                    cursor: 0,
                    search_text: product_name,
                }).then(resp => ({
                    product_name,
                    products: resp.data.catalog_products || []
                }))
                    .catch(err => {
                        console.error(`Error searching for product "${product_name}":`, err);
                        return { product_name, products: [] }; // Return empty array on error to continue with other searches
                    })
            );

            // Wait for all searches to complete
            Promise.all(searchPromises)
                .then(searchResults => {
                    // Structure allProducts as { productName: [cartItem (if exists), ...searchResults] }
                    // Preserve insertion order by iterating through searchable_product_names in order
                    const allProducts = {};

                    searchable_product_names.forEach(product_name => {
                        // Find the search results for this product name
                        const searchResult = searchResults.find(result => result.product_name === product_name);
                        const searchProducts = searchResult?.products || [];

                        // Get the cp_id from make_my_order_request for this product
                        const requestData = cart.data.make_my_order_request[product_name];
                        const requested_cp_id = requestData?.cp_id;

                        // Find existing cart item if it matches the requested cp_id
                        let cartItem = null;
                        let cartItemProductId = null;
                        if (requested_cp_id && cart_items[requested_cp_id]) {
                            cartItem = cart_items[requested_cp_id];
                            cartItemProductId = cartItem.catalog_product?._id;
                        } else if(!requested_cp_id) {
                            // Try to find a cart item that matches one of the found products for the given product_name
                                for (const sp of searchProducts) {
                                    // check if this candidateProduct is already in the cart_items
                                    if (sp?._id && cart_items[sp._id]) {
                                        cartItem = cart_items[sp._id] ? cart_items[sp._id].diff_qty > 0 ? cart_items[sp._id] : null : null;
                                        cartItemProductId = cartItem ? cartItem.catalog_product?._id : null;
                                        break;
                                    }
                            }
                        }

                        // Filter out cart item from search results to avoid duplicates
                        const filteredSearchProducts = cartItemProductId
                            ? searchProducts.filter(product => product._id !== cartItemProductId
                                && !(delivery_route?.other_config?.hide_out_of_stock_products && (!product?.qty || product.qty <= 0)))
                            : searchProducts.filter(product => !(delivery_route?.other_config?.hide_out_of_stock_products && (!product?.qty || product.qty <= 0)));

                        // Build the products array: cart item first (if exists), then filtered search results
                        const products = [];
                        if (cartItem) {
                            products.push(cartItem);
                        }
                        products.push(...filteredSearchProducts);

                        allProducts[product_name] = products;
                    });

                    // Preserve any existing product names that might have been added but not yet searched
                    // This ensures new product names show up immediately with empty arrays
                    Object.keys(cart.data.make_my_order_request).forEach(product_name => {
                        if (!(product_name in allProducts)) {
                            allProducts[product_name] = [];
                        }
                    });

                    // Store allProducts in state
                    setAllProducts(allProducts);
                    setLoading(false);
                })
                .catch(err => {
                    console.error('Error in product search:', err);
                    setLoading(false);
                });
        }
    }, [cart?.data?.make_my_order_request, cart?.delivery_route_id]);


    const handleProductSelect = (product) => {
        // Check if it's a cart item (has catalog_product property) or a search result
        const catalog_product = product.catalog_product || product;
        addProduct({ products: [catalog_product] });
    }

    // Helper function to check if a product name exists (case-insensitive)
    const productNameExists = (product_name) => {
        const normalizedName = product_name.toLowerCase();
        // Check in allProducts
        const existingNames = Object.keys(allProducts);
        if (existingNames.some(name => name.toLowerCase() === normalizedName)) {
            return true;
        }
        // Check in make_my_order_request
        const requestNames = Object.keys(cart?.data?.make_my_order_request || {});
        if (requestNames.some(name => name.toLowerCase() === normalizedName)) {
            return true;
        }
        return false;
    }

    // Helper function to check if an item has products in cart
    const hasProductsInCart = (product_name) => {
        const requestData = cart?.data?.make_my_order_request?.[product_name];
        const requested_cp_id = requestData?.cp_id;
        return requested_cp_id && cart_items[requested_cp_id] && cart_items[requested_cp_id].diff_qty > 0;
    }

    const handleManageItems = () => {
        const initialItems = Object.keys(cart?.data?.make_my_order_request || {});
        const popup = Popup.show("Manage Items", (
            <ManageItemsPopup
                initialItems={initialItems}
                cart={cart}
                cart_items={cart_items}
                allProducts={allProducts}
                setAllProducts={setAllProducts}
                processed_request_ref={processed_request_ref}
                onClose={() => popup.close()}
            />
        ), { container });
    }

    const handleAddNewItem = () => {
        const inputRef = { current: null };
        const addProductName = (popup) => {
            const product_name = inputRef.current?.value?.trim() || '';

            if (!product_name) {
                Popup.toast(
                    <div className='tw-rounded tw-p-2 tw-text-sm'>Please enter a product name.</div>,
                    2500
                );
                return;
            }

            // Check if this product name already exists (case-insensitive)
            if (productNameExists(product_name)) {
                Popup.toast(
                    <div className='tw-rounded tw-p-2 tw-text-sm'>This item already exists in the list.</div>,
                    1500
                );
                return;
            }

            // Add product name to allProducts with empty array (will be populated by useEffect)
            setAllProducts(prev => ({
                [product_name]: [],
                ...prev
            }));

            // Update cart.data.make_my_order_request with new product name
            const updatedMakeMyOrderRequest = {
                [product_name]: {},
                ...(cart.data?.make_my_order_request || {})
            };

            const updatedCart = {
                ...cart,
                data: {
                    ...cart.data,
                    make_my_order_request: updatedMakeMyOrderRequest
                }
            };

            // Reset processed_request_ref to allow useEffect to re-run
            processed_request_ref.current = null;

            broadcaster.broadcast_event("cart_updated", updatedCart);
            popup.close();
        };

        const popup = Popup.show("", (
            <div className='tw-p-4'>
                <div className='tw-mb-4 tw-text-lg tw-font-semibold'>Add New Item</div>
                <div className='tw-flex tw-gap-2'>
                    <input
                        ref={(el) => { inputRef.current = el; }}
                        type='text'
                        placeholder='Enter product name...'
                        className='tw-flex-1 tw-border tw-px-4 tw-py-2 tw-rounded tw-border-gray-300 tw-outline-0 focus:tw-ring-2 focus:tw-ring-blue-300'
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                addProductName(popup);
                            }
                        }}
                    />
                    <button
                        type='button'
                        onClick={() => addProductName(popup)}
                        className='tw-px-6 tw-py-2 tw-bg-secondary-lighter tw-text-white tw-rounded hover:tw-bg-secondary-lighter/90 tw-transition-colors tw-font-medium'
                    >
                        Add
                    </button>
                </div>
            </div>
        ));
    }

    useEffect(() => {
        broadcaster.add_event_listener("makemyorder:new_item", handleAddNewItem);
        return () => {
            broadcaster.remove_event_listener("makemyorder:new_item", handleAddNewItem);
        }
    }, []);

    const startEditingProductName = (product_name) => {
        if (hasProductsInCart(product_name)) {
            Popup.toast(
                <div className='tw-rounded tw-p-2 tw-text-sm'>Cannot edit item name. Related product is already in cart.</div>,
                2000
            );
            return;
        }
        
        setEditingProductName(product_name);
        setEditedProductName(product_name);
        // Focus input after state update
        setTimeout(() => {
            editInputRef.current?.focus();
        }, 0);
    }

    const cancelEditingProductName = () => {
        setEditingProductName(null);
        setEditedProductName('');
    }

    const saveProductName = async (oldProductName) => {
        const newProductName = editedProductName.trim();

        if (!newProductName || newProductName === oldProductName) {
            cancelEditingProductName();
            return;
        }

        // Check if the new product name already exists (case-insensitive, excluding the current one)
        const normalizedNewName = newProductName.toLowerCase();
        const normalizedOldName = oldProductName.toLowerCase();
        const existingNames = Object.keys(allProducts);
        const requestNames = Object.keys(cart?.data?.make_my_order_request || {});
        
        const duplicateExists = [...existingNames, ...requestNames].some(name => 
            name.toLowerCase() === normalizedNewName && name.toLowerCase() !== normalizedOldName
        );

        if (duplicateExists) {
            Popup.toast(
                <div className='tw-rounded tw-p-2 tw-text-sm'>This item already exists in the list.</div>,
                2500
            );
            cancelEditingProductName();
            return;
        }

        setLoading(true);

        try {
            // Call search API with new product name
            const resp = await axios.post(`/api/products/search/${cart.delivery_route_id}`, {
                cursor: 0,
                search_text: newProductName,
            });

            const searchProducts = resp.data.catalog_products || [];

            // Get the cp_id from make_my_order_request for the old product name
            const requestData = cart.data.make_my_order_request[oldProductName];
            const requested_cp_id = requestData?.cp_id;

            // Find existing cart item if it matches the requested cp_id
            let cartItem = null;
            let cartItemProductId = null;
            if (requested_cp_id && cart_items[requested_cp_id]) {
                cartItem = cart_items[requested_cp_id];
                cartItemProductId = cartItem.catalog_product?._id;
            }

            // Filter out cart item from search results to avoid duplicates
            const filteredSearchProducts = cartItemProductId
                ? searchProducts.filter(product => product._id !== cartItemProductId)
                : searchProducts;

            // Build the products array: cart item first (if exists), then filtered search results
            const products = [];
            if (cartItem) {
                products.push(cartItem);
            }
            products.push(...filteredSearchProducts);

            // Update allProducts with new product name (preserving order)
            const allProductsKeys = Object.keys(allProducts);
            const oldIndex = allProductsKeys.indexOf(oldProductName);
            const updatedAllProducts = {};
            allProductsKeys.forEach((key, index) => {
                if (key === oldProductName) {
                    updatedAllProducts[newProductName] = products;
                } else {
                    updatedAllProducts[key] = allProducts[key];
                }
            });
            setAllProducts(updatedAllProducts);

            // Update section refs
            if (sectionRefs.current[oldProductName]) {
                sectionRefs.current[newProductName] = sectionRefs.current[oldProductName];
                delete sectionRefs.current[oldProductName];
            }

            // Update cart.data.make_my_order_request with new product name (preserving order)
            const oldRequest = cart.data.make_my_order_request;
            const requestKeys = Object.keys(oldRequest);
            const requestOldIndex = requestKeys.indexOf(oldProductName);
            const updatedMakeMyOrderRequest = {};
            requestKeys.forEach((key, index) => {
                if (key === oldProductName) {
                    updatedMakeMyOrderRequest[newProductName] = oldRequest[oldProductName];
                } else {
                    updatedMakeMyOrderRequest[key] = oldRequest[key];
                }
            });

            // Update cart with new make_my_order_request
            const updatedCart = {
                ...cart,
                data: {
                    ...cart.data,
                    make_my_order_request: updatedMakeMyOrderRequest
                }
            };
            broadcaster.broadcast_event("cart_updated", updatedCart);

            setEditingProductName(null);
            setEditedProductName('');
        } catch (err) {
            Popup.show("Error", "Failed to search for products. Please try again.");
        } finally {
            setLoading(false);
        }
    }

    const scrollToSection = (product_name) => {
        const sectionRef = sectionRefs.current[product_name];
        if (sectionRef) {
            sectionRef.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }

    if (!cp_ids?.length) {
        return (
            <NotFoundView image="https://storage.googleapis.com/sukhiba/mobilefresh/user_uploads/2024-02-02/1706889389Qivh2BmKSo__cartempty.png"
                title={`${buying_for_user?._id ? `${buying_for_user.name}'s` : 'Your'} List is Empty! Add Item to build your cart now!`} height="100px"
            />
        )
    }

    const additional_costs = cart?.pricing?.additional_costs
    const discounts = cart?.pricing?.discounts
    ctx.custom_discount = discounts?.[CUSTOM_DISCOUNT_CODE] || {};
    ctx.home_delivery_fee = additional_costs?.home_delivery_fee?.amount;

    return (
        <div ref={view_ref} className={cp_ids.length > 0 ? 'tw-pb-24' : 'tw-pb-48'}>
            {is_loading ? <LoadingOverlay /> : null}
            <div className={!container ? 'md:tw-px-4' : ''}>
                {/* Horizontal Scrollable List of Items at Top */}
                {Object.keys(allProducts).length > 0 && (
                    <div className='tw-mb-2 tw-p-3'>
                        <div className='tw-flex tw-items-center tw-justify-between tw-mb-2'>
                            <div className='tw-text-xs tw-text-gray-600'>
                                We found options for {Object.keys(allProducts).length} items. Tap to choose what you want.
                            </div>
                            {/* <button
                                type='button'
                                onClick={handleManageItems}
                                className='tw-px-3 tw-py-1 tw-text-xs tw-font-medium tw-text-secondary-lighter tw-border tw-border-secondary-lighter tw-rounded hover:tw-bg-secondary-lighter/10 tw-transition-colors tw-flex tw-items-center tw-gap-1'
                            >
                                <PencilLine size={12} />
                                Manage
                            </button> */}
                        </div>
                        <div className='tw-overflow-x-auto w3-hide-scrollbar'>
                            <div className='tw-flex tw-gap-2' style={{ minWidth: 'max-content' }}>
                                {Object.keys(allProducts).map((product_name) => {

                                    return (
                                        <button
                                            key={product_name}
                                            type='button'
                                            onClick={() => scrollToSection(product_name)}
                                            className='tw-flex-shrink-0 tw-px-4 tw-py-1 tw-bg-white tw-border tw-border-gray-300 tw-rounded-full tw-text-sm tw-font-medium tw-text-gray-700 hover:tw-bg-gray-50 hover:tw-border-gray-400 tw-transition-colors tw-flex tw-items-center tw-gap-2'
                                        >
                                            <span>{product_name}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}
                {/* Display product sections from make_my_order_request */}
                {Object.keys(allProducts).length > 0 && (
                    <div className='tw-space-y-2'>
                        {Object.entries(allProducts).map(([product_name, products]) => {
                            const requestData = cart?.data?.make_my_order_request?.[product_name];

                            return (
                                <div
                                    key={product_name}
                                    ref={(el) => {
                                        if (el) {
                                            sectionRefs.current[product_name] = el;
                                        }
                                    }}
                                    className='tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-overflow-hidden'
                                >
                                    {/* Section Header */}
                                    <div className='tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-bg-gray-50 tw-border-b tw-border-gray-200'>
                                        {editingProductName === product_name ? (
                                            /* Edit Mode */
                                            <div className='tw-flex tw-items-center tw-gap-2 tw-flex-1'>
                                                <input
                                                    ref={editInputRef}
                                                    type='text'
                                                    value={editedProductName}
                                                    onChange={(e) => setEditedProductName(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            saveProductName(product_name);
                                                        } else if (e.key === 'Escape') {
                                                            cancelEditingProductName();
                                                        }
                                                    }}
                                                    className='tw-flex-1 tw-px-3 tw-py-1.5 tw-text-base tw-font-semibold tw-border tw-border-blue-500 tw-rounded tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-300'
                                                />
                                                <button
                                                    type='button'
                                                    onClick={() => saveProductName(product_name)}
                                                    className='tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-bg-green-600 tw-text-white tw-rounded hover:tw-bg-green-700 tw-transition-colors'
                                                >
                                                    <Check size={18} />
                                                </button>
                                                <button
                                                    type='button'
                                                    onClick={cancelEditingProductName}
                                                    className='tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-text-gray-700 hover:tw-text-gray-900'
                                                >
                                                    <X size={18} />
                                                </button>
                                            </div>
                                        ) : (
                                            /* View Mode */
                                            <>
                                                <div className='tw-flex tw-items-center tw-gap-2 tw-flex-1'>
                                                    <h3 className='tw-text-base tw-font-semibold tw-text-gray-900'>
                                                        {product_name}
                                                    </h3>
                                                </div>
                                                {/* <PencilLine
                                                    size={16}
                                                    className={`${
                                                        hasProductsInCart(product_name)
                                                            ? 'tw-text-gray-300 tw-cursor-not-allowed'
                                                            : 'tw-text-gray-500 hover:tw-text-gray-700 tw-cursor-pointer'
                                                    }`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        startEditingProductName(product_name);
                                                    }}
                                                    title={hasProductsInCart(product_name) ? 'Cannot edit item name with products in cart' : 'Edit item name'}
                                                /> */}
                                            </>
                                        )}
                                    </div>

                                    {/* Products List - Horizontal Scrollable */}
                                    {products.length > 0 && (
                                        <div className='tw-overflow-x-auto tw-p-4'>
                                            <div className='tw-flex tw-gap-4' style={{ minWidth: 'max-content' }}>
                                                {products.map((product) => {
                                                    // Handle both cart items and search result products
                                                    const catalog_product = product.catalog_product || product;
                                                    const isCartItem = !!product.catalog_product || cart_items[product._id]?.diff_qty > 0;
                                                    const uniqueKey = isCartItem
                                                        ? `${product_name}-cart-${catalog_product._id}`
                                                        : `${product_name}-search-${catalog_product._id}`;

                                                    return (
                                                        <div
                                                            key={uniqueKey}
                                                            className={`tw-flex-shrink-0 tw-w-[200px] tw-border ${isCartItem ? 'tw-border-2 tw-border-secondary-lighter' : 'tw-border-gray-200'} tw-rounded-lg tw-overflow-hidden tw-bg-white`}
                                                        >
                                                            <CatalogProduct
                                                                delivery_route={delivery_route}
                                                                buying_for_user={buying_for_user}
                                                                catalog_product={catalog_product}
                                                                price={catalog_product.price}
                                                                cart={cart}
                                                                grid={true}
                                                                image_height={120}
                                                                onViewProduct={onViewProduct}
                                                                className={isCartItem ? '' : ''}
                                                            />
                                                            {!isCartItem && (
                                                                <div className='tw-p-2 tw-border-t tw-border-gray-200'>
                                                                    <button
                                                                        type='button'
                                                                        onClick={() => handleProductSelect(catalog_product)}
                                                                        className='tw-w-full tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-border tw-border-gray-300 tw-bg-white tw-rounded hover:tw-bg-gray-50 tw-transition-colors'
                                                                    >
                                                                        + ADD
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* View Cart Button - Fixed at Bottom */}
            {cp_ids.length > 0 && (
                <div className='tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-bg-white tw-border-t tw-border-gray-200 tw-shadow-lg tw-z-50'>
                    <div className='tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-max-w-full'>
                        {/* Left Side - Totals */}
                        <div className='tw-flex tw-flex-col tw-gap-1 tw-flex-1 tw-min-w-0'>
                            <div className='tw-text-xxs tw-mb-1 tw-text-gray-600'>
                                {cp_ids.length} Products
                            </div>
                            {total_amount > 0 && (
                                <div className='tw-text-base tw-font-bold tw-text-gray-900'>
                                    {getDisplayPrice(currency, total_amount)}
                                </div>
                            )}

                            {total_saving > 100 && (
                                <div className='tw-text-xs tw-text-green-600 tw-font-medium'>
                                    You save {getDisplayPrice(currency, total_saving)}
                                </div>
                            )}
                        </div>

                        {/* Right Side - View Cart Button */}
                        <button
                            type='button'
                            onClick={() => {
                                broadcaster.broadcast_event("view_cart", {});
                            }}
                            className='tw-bg-secondary-lighter tw-rounded-md tw-px-6 tw-py-2 tw-text-white tw-text-sm'                        >
                            View Cart
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}

export default MakeMyCart
